import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  VideoCameraIcon, 
  StopIcon, 
  MicrophoneIcon
} from '@heroicons/react/24/outline';

const MediaRecorder = ({ 
  onMediaRecorded, 
  onCancel, 
  mode = 'video', 
  onSubmit, 
  previewOnly = false,
  autoStart = false,
  permissionAlreadyGranted = false,
  timerExpired = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [stream, setStream] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [hasStopped, setHasStopped] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showReRecordConfirm, setShowReRecordConfirm] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const intervalRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const transcriptRef = useRef(''); // Silent transcription
  const reviewPlayerRef = useRef(null);
  const resumePendingRef = useRef(false); // Flag: start recording after next state flush
  const streamRef = useRef(null); // Mirror of stream state for unmount cleanup

  // Keep streamRef in sync so the unmount cleanup can access the latest stream
  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  // Stream / timer / speech cleanup — only on unmount
  // Uses streamRef (not stream state) so changing recordingUrl never triggers this
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke blob URL when it changes or component unmounts
  useEffect(() => {
    return () => {
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
    };
  }, [recordingUrl]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(intervalRef.current);
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
      setHasStopped(true);
    }
  }, [isRecording]);

  // Add max duration timer for 5 minutes (300s)
  useEffect(() => {
    if (isRecording) {
      const maxTimer = setTimeout(() => {
        if (isRecording) {
          stopRecording();
        }
      }, 300000); // 5 minutes
      return () => clearTimeout(maxTimer);
    }
  }, [isRecording, stopRecording]);

  const startMedia = useCallback(async () => {
    setError('');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: mode === 'video',
        audio: true
      });
      setStream(mediaStream);
      setReady(true);
      setShowPreview(true);
      if (videoRef.current && mode === 'video') {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      setError(`Unable to access ${mode === 'video' ? 'camera' : 'microphone'}. Please check your permissions.`);
    }
  }, [mode]);

  // Pick the best supported MIME type for the browser
  const getBestMimeType = useCallback(/** @param {'video'|'audio'} recordingMode */ (recordingMode) => {
    const videoTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    const audioTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
    ];
    const types = recordingMode === 'video' ? videoTypes : audioTypes;
    return types.find(type => window.MediaRecorder.isTypeSupported(type)) || '';
  }, []);

  const startRecording = useCallback(() => {
    if (!stream) return;

    const mimeType = getBestMimeType(mode);
    try {
      mediaRecorderRef.current = new window.MediaRecorder(stream, mimeType ? { mimeType } : {});
    } catch (error) {
      console.error('MediaRecorder not supported:', error);
      alert('Recording is not supported in this browser.');
      return;
    }

    const chunks = [];
    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      // Use the actual mimeType from the recorder (most accurate)
      const blobType = mediaRecorderRef.current?.mimeType || 
                       (mode === 'video' ? 'video/webm' : 'audio/webm');
      const blob = new Blob(chunks, { type: blobType });
      setMediaBlob(blob);
      // Create URL for review player
      const url = URL.createObjectURL(blob);
      setRecordingUrl(url);
    };

    // 🤫 SILENT transcription - no UI feedback
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      speechRecognitionRef.current = new SpeechRecognition();
      
      speechRecognitionRef.current.continuous = true;
      speechRecognitionRef.current.interimResults = false;
      speechRecognitionRef.current.maxAlternatives = 1;
      speechRecognitionRef.current.lang = 'en-US';

      transcriptRef.current = '';
      setTranscript('');

      speechRecognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + ' ';
          }
        }
        
        if (finalTranscript.trim()) {
          transcriptRef.current += finalTranscript;
          setTranscript(transcriptRef.current.trim());
          
          // 🤫 Silent logging - only in console for debugging
          console.log('📝 Silent transcription:', {
            word_count: transcriptRef.current.split(' ').filter(w => w.length > 0).length,
            length: transcriptRef.current.length
          });
        }
      };

      speechRecognitionRef.current.onerror = (event) => {
        // 🤫 Silent error handling - don't show transcription errors to user
        console.warn('Silent transcription error:', event.error);
      };

      speechRecognitionRef.current.onend = () => {
        // 🤫 Silent end - no UI feedback
      };

      speechRecognitionRef.current.start();
    }

    mediaRecorderRef.current.start();
    setIsRecording(true);
    setDuration(0);

    intervalRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  }, [stream, mode, getBestMimeType]);

  // Auto-start media when autoStart is enabled
  useEffect(() => {
    if (autoStart && permissionAlreadyGranted && !ready && !previewOnly) {
      startMedia();
    } else if (previewOnly && !ready) {
      startMedia();
    }
  }, [autoStart, permissionAlreadyGranted, ready, previewOnly, startMedia]);

  // Auto-start recording once media is ready (initial start only, not after resume)
  useEffect(() => {
    if (autoStart && ready && !isRecording && !hasStopped && !previewOnly && !resumePendingRef.current) {
      setTimeout(() => startRecording(), 500);
    }
  }, [autoStart, ready, isRecording, hasStopped, previewOnly, startRecording]);

  // Fire startRecording after a resume state-reset has flushed
  useEffect(() => {
    if (resumePendingRef.current && ready && !hasStopped && !isRecording) {
      resumePendingRef.current = false;
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
      }
      startRecording();
    }
  }, [ready, hasStopped, isRecording, stream, startRecording]);

  // Stop recording if timerExpired prop is true
  useEffect(() => {
    if (timerExpired && isRecording) {
      stopRecording();
    }
  }, [timerExpired, isRecording, stopRecording]);

  useEffect(() => {
    if (mode === 'video' && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, mode]);

  // showConfirmation state kept for potential future use but no longer auto-triggered

  // Re-record: show confirmation then discard current clip and restart
  const handleReRecordConfirm = useCallback(() => {
    setShowReRecordConfirm(false);
  }, []);

  const handleResume = useCallback(() => {  // kept as internal fn name for state machinery
    // URL revocation is handled by the recordingUrl cleanup effect
    // Signal that startRecording should be called once state resets
    resumePendingRef.current = true;
    // Reset all recording state
    setHasStopped(false);
    setMediaBlob(null);
    setRecordingUrl(null);
    setDuration(0);
    setTranscript('');
    transcriptRef.current = '';
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setReady(true);
    setShowPreview(true);
  }, []);

  // Submit handler with silent transcription
  const handleSubmitConfirmed = () => {
    const finalTranscript = transcriptRef.current.trim();
    
    const result = {
      duration,
      transcription: finalTranscript, // Backend gets it
      answer_text: finalTranscript || 'Media response provided',
      transcription_method: 'browser',
      word_count: finalTranscript.split(' ').filter(word => word.length > 0).length,
    };
    
    if (mode === 'video') {
      result.videoBlob = mediaBlob;
      result.audioBlob = mediaBlob;
    } else {
      result.audioBlob = mediaBlob;
    }
    
    // 🤫 Silent submission - log for debugging only
    console.log('📤 Submitting with silent transcription:', {
      has_transcription: !!finalTranscript,
      word_count: result.word_count,
      duration: duration,
      has_media: !!mediaBlob
    });
    
    if (onSubmit) {
      onSubmit(result);
    } else if (onMediaRecorded) {
      onMediaRecorded(result);
    }
  };

  const handleSubmit = () => {
    // Confirmation modal is auto-shown when recording stops
    // This handler is deprecated but kept for manual submit if needed
    setShowConfirmation(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {previewOnly 
            ? (mode === 'video' ? 'Video Interview Preview' : 'Audio Interview Preview')
            : (mode === 'video' ? 'Video Response' : 'Audio Response')
          }
        </h3>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-2 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {/* Live camera — always visible once stream is active */}
      {mode === 'video' && showPreview && (
        <div className="relative bg-gray-900 rounded-lg overflow-hidden aspect-video">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{ background: '#222', minHeight: 240 }}
            onLoadedMetadata={() => {
              if (videoRef.current) {
                videoRef.current.play().catch(() => {});
              }
            }}
          />
          {!stream && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-lg bg-black bg-opacity-40">
              {autoStart ? 'Starting camera...' : 'Camera not enabled'}
            </div>
          )}
          {/* Recording indicator */}
          {isRecording && (
            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">
              ● {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
            </div>
          )}
          {/* Captured badge */}
          {hasStopped && !isRecording && (
            <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
              ✓ Captured {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      )}
      
      {mode === 'audio' && showPreview && (
        <div className="bg-gray-100 rounded-lg p-12 text-center">
          <div className="flex flex-col items-center space-y-6">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${isRecording ? 'bg-red-100' : hasStopped ? 'bg-green-100' : 'bg-gray-200'}`}>
              <MicrophoneIcon className={`w-12 h-12 ${isRecording ? 'text-red-600 animate-pulse' : hasStopped ? 'text-green-600' : 'text-gray-500'}`} />
            </div>
            {isRecording && (
              <div className="text-red-600 font-medium">
                Recording: {Math.floor(duration / 60)}:{(duration % 60).toString().padStart(2, '0')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      {!previewOnly && (
        <div className="flex flex-col items-center gap-4">
          {/* Show initialization message for auto-start mode */}
          {autoStart && !ready && (
            <div className="text-center">
              <div className="text-sm text-gray-600">
                Starting {mode === 'video' ? 'camera' : 'microphone'}...
              </div>
            </div>
          )}
          
          {/* Manual start button - only show if NOT auto-start */}
          {!autoStart && !ready && (
            <button
              onClick={startMedia}
              className="btn-primary w-full max-w-sm flex items-center justify-center space-x-2 text-base py-3"
            >
              {mode === 'video' ? (
                <>
                  <VideoCameraIcon className="w-5 h-5" />
                  <span>Start Recording</span>
                </>
              ) : (
                <>
                  <MicrophoneIcon className="w-5 h-5" />
                  <span>Start Recording</span>
                </>
              )}
            </button>
          )}
          
          {/* Manual recording start - only show if NOT auto-start */}
          {!autoStart && ready && !isRecording && !hasStopped && (
            <button
              onClick={startRecording}
              className="btn-primary w-full max-w-sm flex items-center justify-center space-x-2 text-base py-3"
            >
              <VideoCameraIcon className="w-5 h-5" />
              <span>Start Recording</span>
            </button>
          )}
          
          {/* Stop recording button - show when recording */}
          {isRecording && (
            <button
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 text-white font-medium w-full max-w-sm py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-base"
            >
              <StopIcon className="w-5 h-5" />
              <span>Stop Recording</span>
            </button>
          )}
          
          {/* Post-stop actions — shown directly below the live camera */}
          {hasStopped && mediaBlob && !showReRecordConfirm && (
            <div className="w-full max-w-sm flex gap-3">
              <button
                onClick={() => setShowReRecordConfirm(true)}
                className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-medium py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <span>Re-record</span>
              </button>
              <button
                onClick={handleSubmitConfirmed}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <span>Submit Answer</span>
              </button>
            </div>
          )}

          {/* Re-record confirmation */}
          {hasStopped && mediaBlob && showReRecordConfirm && (
            <div className="w-full max-w-sm bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
              <p className="text-sm font-semibold text-amber-800 mb-1">Discard this recording?</p>
              <p className="text-xs text-amber-700 mb-4">
                Your {Math.floor(duration / 60)}:{String(duration % 60).padStart(2, '0')} recording will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReRecordConfirm(false)}
                  className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 rounded-lg transition-colors text-sm"
                >
                  Keep it
                </button>
                <button
                  onClick={() => { handleReRecordConfirm(); handleResume(); }}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded-lg transition-colors text-sm"
                >
                  Yes, re-record
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation dialog removed - submit directly from review screen */}

      {/* 🧹 REMOVED: All transcription UI feedback */}
      
      {/* Clean, focused guidance text */}
      <div className="text-sm text-gray-600 text-center space-y-2">
        <p>
          {previewOnly
            ? `This is a preview. Your ${mode === 'video' ? 'camera' : 'microphone'} will be used for the interview.`
            : autoStart && !ready 
            ? `Starting ${mode === 'video' ? 'camera' : 'microphone'}...`
            : autoStart && ready && !isRecording && !hasStopped
            ? `Recording will start automatically...`
            : !ready 
            ? `Click the button above to start recording your answer.`
            : isRecording 
            ? `Recording in progress - ${mode === 'video' ? 'speak clearly and maintain eye contact' : 'speak clearly into your microphone'}`
            : hasStopped 
            ? 'Review your answer below, then submit or re-record a new one.'
            : ''
          }
        </p>
      </div>
    </div>
  );
};

export default MediaRecorder;